/**
 * Cookie consent banner — GDPR/CCPA compliant gate for marketing scripts.
 *
 * Stores the user's choice in localStorage. `MarketingScripts` reads
 * `getConsent()` and only injects GTM / Meta / TikTok when consent === "granted".
 * Essential scripts (favicon, first-party analytics) always run.
 */
import React, { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const KEY = "endag1_cookie_consent";  // "granted" | "denied"

export function getConsent() {
  try { return localStorage.getItem(KEY); } catch { return null; }
}

function setConsent(v) {
  try { localStorage.setItem(KEY, v); } catch { /* private-mode: ignore */ }
  // Bubble via custom event so MarketingScripts can re-render
  window.dispatchEvent(new Event("endag1_consent_change"));
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Give the page a beat before showing so it doesn't clash with initial paint
    const t = setTimeout(() => {
      if (!getConsent()) setVisible(true);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const decide = (choice) => { setConsent(choice); setVisible(false); };

  return (
    <div
      data-testid="cookie-consent"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[70] border border-white/15 bg-[#0F0F10]/95 backdrop-blur-xl p-5 shadow-2xl"
    >
      <div className="flex items-start gap-3">
        <Cookie className="h-5 w-5 text-[#4ADE80] shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4ADE80] mb-1">Cookies</div>
          <p className="text-sm text-[#E5E5E5] leading-relaxed">
            We use cookies for essential functionality and — with your consent — analytics/marketing to improve the store.{" "}
            <a href="/privacy" className="underline underline-offset-4 hover:text-[#4ADE80]">Learn more</a>
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              data-testid="cookie-accept"
              onClick={() => decide("granted")}
              className="btn-press bg-[#4ADE80] text-[#0F0F10] px-4 py-2 text-[11px] uppercase tracking-[0.14em] font-semibold hover:bg-[#6FE998]"
            >
              Accept all
            </button>
            <button
              data-testid="cookie-decline"
              onClick={() => decide("denied")}
              className="btn-press border border-white/20 text-[#F5F5F5] px-4 py-2 text-[11px] uppercase tracking-[0.14em] hover:border-white/40"
            >
              Essential only
            </button>
          </div>
        </div>
        <button
          data-testid="cookie-close"
          aria-label="Dismiss"
          onClick={() => decide("denied")}
          className="text-[#7a7a7a] hover:text-[#F5F5F5]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
